import { useState, useEffect } from "react";
import { makeTr } from "@/i18n/tr";
import { BlogFeed } from "@/components/blog/BlogFeed";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStore } from "@/store/useStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Plus, 
  Clock, 
  Coins,
  User,
  Loader2,
  RefreshCw,
  Gamepad2,
  Bot
} from "lucide-react";

export default function LobbyPage() {
  const { t, i18n } = useTranslation();
  const isRu = i18n.language === "ru";
  const tr = makeTr(i18n.language);
  const navigate = useNavigate();
  const { user, availableGames, fetchAvailableGames, createGame, joinGame } = useStore();
  
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Create game form
  const [stake, setStake] = useState("0");
  const [myColor, setMyColor] = useState("random");
  const [timeControl, setTimeControl] = useState("600");
  const [increment, setIncrement] = useState("0");

  useEffect(() => {
    loadGames();
    const interval = setInterval(loadGames, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const loadGames = async () => {
    await fetchAvailableGames();
    setLoading(false);
  };

  const handleCreateGame = async () => {
    const stakeValue = parseFloat(stake) || 0;
    
    if (stakeValue > (user?.balance || 0)) {
      toast.error(t("wallet.insufficientBalance"));
      return;
    }

    setCreating(true);
    const result = await createGame(stakeValue, parseInt(timeControl), parseInt(increment), myColor);
    
    if (result.success) {
      toast.success(t("common.success"), {
        description: t("game.waiting")
      });
      setDialogOpen(false);
      navigate(`/game/${result.game.id}`);
    } else {
      toast.error(t("common.error"), {
        description: result.error
      });
    }
    setCreating(false);
  };

  const handleJoinGame = async (gameId) => {
    const game = availableGames.find(g => g.id === gameId);
    
    if (game?.stake > (user?.balance || 0)) {
      toast.error(t("wallet.insufficientBalance"));
      return;
    }

    setJoining(gameId);
    const result = await joinGame(gameId);
    
    if (result.success) {
      navigate(`/game/${gameId}`);
    } else {
      toast.error(t("common.error"), {
        description: result.error
      });
    }
    setJoining(null);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    return `${mins} ${t("common.minutes")}`;
  };

  const timeOptions = [
    { value: "60", label: "1 min (Bullet)" },
    { value: "180", label: "3 min (Blitz)" },
    { value: "300", label: "5 min (Blitz)" },
    { value: "600", label: "10 min (Rapid)" },
    { value: "900", label: "15 min (Rapid)" },
    { value: "1800", label: "30 min (Classical)" },
  ];

  return (
    <div className="space-y-4 px-3 pb-4" data-testid="lobby-page">
      {/* TeaChess Logo Header - Compact */}
      <div className="flex items-center justify-center py-3 border-b border-border/50">
        <div className="flex items-center gap-2">
          <span className="text-primary text-xl">♛</span>
          <span className="text-lg font-black tracking-tight">
            TEA<span className="text-primary">CHESS</span>
          </span>
        </div>
      </div>

      {/* Main Action Buttons - 2x2 Grid */}
      <div className="grid grid-cols-2 gap-3">
        {/* Play with Bot */}
        <Button 
          onClick={() => navigate("/play-bot")}
          variant="outline"
          className="h-20 flex flex-col items-center justify-center gap-1 text-sm font-medium border-2 hover:border-primary/50 hover:bg-primary/5"
          data-testid="play-bot-btn"
        >
          <Bot className="h-6 w-6 text-muted-foreground" />
          <span>{tr("С ботом", "vs Bot")}</span>
        </Button>

        {/* Play for Money */}
        <Button 
          onClick={() => navigate("/tournaments")}
          className="h-20 flex flex-col items-center justify-center gap-1 text-sm font-medium bg-gradient-to-br from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black border-0"
          data-testid="play-money-btn"
        >
          <Coins className="h-6 w-6" />
          <span>{tr("Турниры на деньги", "Money Tournaments")}</span>
        </Button>

        {/* Create Game */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-1 text-sm font-medium border-2 hover:border-primary/50 hover:bg-primary/5"
              data-testid="create-game-btn"
            >
              <Plus className="h-6 w-6 text-muted-foreground" />
              <span>{tr("Создать", "Create")}</span>
            </Button>
          </DialogTrigger>
              <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{t("game.createGame")}</DialogTitle>
                <DialogDescription>
                  Set up your game parameters
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 pt-4">
                <div className="space-y-2">
                  <Label>{t("game.timeControl")}</Label>
                  <Select value={timeControl} onValueChange={setTimeControl}>
                    <SelectTrigger data-testid="time-control-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {timeOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t("game.increment")} ({t("common.seconds")})</Label>
                  <Select value={increment} onValueChange={setIncrement}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0</SelectItem>
                      <SelectItem value="1">+1</SelectItem>
                      <SelectItem value="2">+2</SelectItem>
                      <SelectItem value="3">+3</SelectItem>
                      <SelectItem value="5">+5</SelectItem>
                      <SelectItem value="10">+10</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{tr("Ваш цвет", "Your color")}</Label>
                  <div className="grid grid-cols-3 gap-2" data-testid="color-picker">
                    {[
                      { id: "white", label: tr("Белые", "White"), style: { background: "#fff" } },
                      { id: "random", label: tr("Случайно", "Random"), style: { background: "linear-gradient(90deg, #fff 50%, #18181b 50%)" } },
                      { id: "black", label: tr("Чёрные", "Black"), style: { background: "#18181b" } },
                    ].map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setMyColor(c.id)}
                        data-testid={`lobby-color-${c.id}`}
                        className={`flex flex-col items-center gap-1.5 py-2.5 rounded-lg border-2 transition-colors ${myColor === c.id ? "border-primary bg-primary/10" : "border-border/50 hover:border-border"}`}
                      >
                        <span className="h-6 w-6 rounded-full border border-zinc-500" style={c.style} />
                        <span className="text-xs font-medium">{c.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t("game.stake")} (Points)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="10"
                    value={stake}
                    onChange={(e) => setStake(e.target.value)}
                    placeholder="0"
                    className="h-12 bg-input/50"
                    data-testid="stake-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    {parseFloat(stake) === 0 ? t("game.noStake") : `${t("game.stake")}: ${stake} Points`}
                  </p>
                </div>

                <Button 
                  onClick={handleCreateGame}
                  className="w-full btn-gold"
                  disabled={creating}
                  data-testid="create-game-submit"
                >
                  {creating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("common.loading")}
                    </>
                  ) : (
                    <>
                      <Gamepad2 className="mr-2 h-4 w-4" />
                      {t("game.createGame")}
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

        {/* Refresh Button */}
        <Button 
          onClick={loadGames}
          disabled={loading}
          variant="outline"
          className="h-20 flex flex-col items-center justify-center gap-1 text-sm font-medium border-2 hover:border-primary/50 hover:bg-primary/5"
        >
          <RefreshCw className={`h-6 w-6 text-muted-foreground ${loading ? "animate-spin" : ""}`} />
          <span>{tr("Обновить", "Refresh")}</span>
        </Button>
      </div>

      {/* Promotional Banners */}
      <div className="space-y-3">
        {/* Banner 1 - Tournaments (clickable) */}
        <div
          onClick={() => navigate("/tournaments")}
          data-testid="banner-tournaments"
          className="relative cursor-pointer hover:from-red-500/30 hover:to-rose-500/30 transition-all active:scale-[0.98] overflow-hidden rounded-xl bg-gradient-to-r from-red-500/20 to-rose-500/20 border border-red-500/30 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center shrink-0">
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-sm">{tr("Турниры с призами", "Tournaments with Prizes")}</h3>
              <p className="text-xs text-muted-foreground">{tr("Swiss и Arena форматы", "Swiss and Arena formats")}</p>
            </div>
            <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>


        {/* Banner 2 - Rating System */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 p-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shrink-0">
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div>
              <h3 className="font-bold text-sm">{tr("Рейтинг ELO и лиги", "ELO Rating & Leagues")}</h3>
              <p className="text-xs text-muted-foreground">{tr("Соревнуйся с игроками своего уровня", "Compete with players of your level")}</p>
            </div>
          </div>
        </div>

        {/* Banner 3 - Referral Program (Clickable) */}
        <div 
          onClick={() => navigate("/referrals")}
          className="relative overflow-hidden rounded-xl bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 p-4 cursor-pointer hover:from-green-500/30 hover:to-emerald-500/30 transition-all active:scale-[0.98]"
        >
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shrink-0">
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-sm">{tr("Приглашай друзей", "Invite Friends")}</h3>
              <p className="text-xs text-muted-foreground">{tr("От 3% с каждого пополнения друзей", "Earn 3%+ from every friend's deposit")}</p>
            </div>
            <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>

      </div>

      {/* News & lessons feed */}
      <BlogFeed />

      {/* Available Games List */}
      {availableGames.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground px-1">
            {tr("Доступные игры", "Available games")} ({availableGames.length})
          </p>
          {availableGames.map((game) => (
            <div 
              key={game.id} 
              className="flex items-center justify-between p-3 rounded-lg bg-card/50 border border-border/50"
              data-testid={`game-card-${game.id}`}
            >
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center">
                  <User className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium">{game.white_username || game.black_username}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {formatTime(game.time_control)}
                    {game.increment > 0 && ` +${game.increment}s`}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-bold text-primary">
                  {game.stake > 0 ? `${game.stake} Points` : "Free"}
                </span>
                <Button 
                  size="sm"
                  onClick={() => handleJoinGame(game.id)}
                  disabled={joining === game.id || game.white_player_id === user?.id}
                  data-testid={`join-game-${game.id}`}
                >
                  {joining === game.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    tr("Играть", "Join")
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
